import {
  BOOLEAN_FIELD_ALIASES,
  DIVIDEND_PAYMENT_TYPE_ALIAS,
  INVESTMENT_DATE_ALIAS,
  SECURITY_TYPE_ALIAS,
} from 'common/constants/cap-table/capTable';
import { PERCENT, PERCENTAGE } from 'common/constants/general';
import { DIVIDEND_PAYMENT_CASH, DIVIDEND_PAYMENT_PIK } from 'common/constants/inputs';
import { LIST as SECURITY_TYPE_LIST } from 'common/constants/securityTypes';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import { finiteNumberOf } from './aiNumericValue';
import { AiDateFields, parseAiDict } from './parseAiDict';

const YES_NO_LABELS = ['No', 'Yes'];

// Prefer earliestGrantDate, then asOfDate. Empty/null fields are skipped.
const pickAiDateDisplay = (fields: AiDateFields | null | undefined): string | undefined =>
  fields?.earliestGrantDate ?? fields?.asOfDate ?? undefined;

/**
 * Investment-date display: plain date strings pass through; Python/JSON dict
 * shapes (and already-parsed objects) extract earliestGrantDate ?? asOfDate.
 * Dict-shaped values with no usable date return '' - never the raw dict /
 * "[object Object]" string that this path exists to hide.
 */
const formatInvestmentDateValue = (val: unknown): string => {
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    return pickAiDateDisplay(val as AiDateFields) ?? '';
  }
  if (typeof val === 'string' && val.trimStart().startsWith('{')) {
    return pickAiDateDisplay(parseAiDict<AiDateFields>(val)) ?? '';
  }
  return String(val);
};

// Decimals a number really has once float noise past 15 significant digits is removed.
const fractionDigitsOf = (num: number): number => {
  const [mantissa, exponent] = Number(num.toPrecision(15)).toExponential().split('e');
  return Math.min(20, Math.max(0, (mantissa.split('.')[1]?.length ?? 0) - Number(exponent)));
};

/**
 * Numeric tail of formatDisplayValue: formats like the grid cell (separators,
 * currency, percent, suffix). Shows every decimal the value has (never fewer
 * than the cell's minimum), or the row format's own decimals with `roundToCellFormat`.
 */
const renderNumber = (
  num: number,
  cellFormat: RowFormat | undefined,
  gridType: string | undefined,
  currencyCode: string | undefined,
  roundToCellFormat: boolean
): string => {
  const percent = !cellFormat?.suffix && (gridType === PERCENTAGE || cellFormat?.style === PERCENT);
  const shown = percent && cellFormat?.isDecimal !== false ? num * 100 : num;
  const exact = Math.max(cellFormat?.minimumFractionDigits ?? 0, fractionDigitsOf(shown));
  const currency = !percent && !cellFormat?.suffix && cellFormat?.style === 'currency';
  const text = new Intl.NumberFormat('en-US', {
    ...(currency && { style: 'currency', currency: currencyCode ?? DEFAULT_CURRENCY }),
    minimumFractionDigits: roundToCellFormat ? cellFormat?.minimumFractionDigits : exact,
    maximumFractionDigits: roundToCellFormat ? cellFormat?.maximumFractionDigits : exact,
  }).format(shown);
  return `${text}${percent ? '%' : (cellFormat?.suffix ?? '')}`;
};

/**
 * Human display for an AI-suggested (or baseline) value, using the CELL's own
 * format config: security-type ids map to their labels, dividend payment type
 * to Cash/PIK, booleans to Yes/No, numbers through the cell's suffix / percent
 * / currency / locale rules. Numbers show exactly as found; pass
 * `roundToCellFormat` where the value stands in for a grid cell's own display.
 *
 * Lives in utilities (not next to the viewers) so reviewAggregator can share
 * it without a component-module cycle (the viewers import reviewStatusOf).
 */
export const formatDisplayValue = (
  val: unknown,
  dbType?: string,
  fieldAlias?: string,
  cellFormat?: RowFormat,
  gridType?: string,
  currencyCode?: string,
  { roundToCellFormat = false }: { roundToCellFormat?: boolean } = {}
): string => {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  if (fieldAlias === INVESTMENT_DATE_ALIAS) {
    return formatInvestmentDateValue(val);
  }
  if (fieldAlias === SECURITY_TYPE_ALIAS) {
    const label = SECURITY_TYPE_LIST[Number(val) - 1];
    return label ?? String(val);
  }
  if (fieldAlias === DIVIDEND_PAYMENT_TYPE_ALIAS) {
    const normalized = typeof val === 'string' ? val.toLowerCase() : val;
    if (normalized === 0 || normalized === DIVIDEND_PAYMENT_CASH.toLowerCase()) {
      return DIVIDEND_PAYMENT_CASH;
    }
    if (normalized === 1 || normalized === DIVIDEND_PAYMENT_PIK.toLowerCase()) {
      return DIVIDEND_PAYMENT_PIK;
    }
  }
  if (dbType === 'boolean' || BOOLEAN_FIELD_ALIASES.has(fieldAlias ?? '')) {
    let idx: number;
    if (typeof val === 'boolean') {
      idx = val ? 1 : 0;
    } else {
      idx = Number(val);
    }
    return YES_NO_LABELS[idx] ?? String(val);
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  // Doc-spelled tokens like "$133.5" format as numbers instead of echoing verbatim.
  const num = finiteNumberOf(val);
  return num === undefined ? String(val) : renderNumber(num, cellFormat, gridType, currencyCode, roundToCellFormat);
};

export default formatDisplayValue;
